import { Box, Paragraph, Heading } from 'grommet'
import styled, { css } from 'styled-components'

import { NormalizedWrapper } from '@theme'

import {
  ContentWrapperProps,
  ContentProps,
  TitleProps,
  SubtitleProps,
  TextProps,
} from './types'

export const Wrapper = styled(NormalizedWrapper)`
  position: relative;
`

const gridForTwoElements = css`
  grid-template-columns: repeat(2, 1fr);

  ${({ theme }) => css`
    ${theme.below.mobileXL`
      grid-template-columns: repeat(1, 1fr);
    `}
  `}
`

const gridForThreeElements = css`
  grid-template-columns: repeat(6, 2fr);

  div {
    grid-column: span 2;
  }

  ${({ theme }) => css`
    ${theme.below.mobileXL`
      grid-template-columns: repeat(2, 1fr);

      div {
        grid-column: span 1;
      }
    `}
  `}
`

const gridForFourElements = css`
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
`

const gridForFiveElements = css`
  grid-template-columns: repeat(6, 2fr);

  div {
    grid-column: span 2;
  }

  div:nth-child(4) {
    grid-column: 1/4;
  }

  div:nth-child(5) {
    grid-column: 4/7;
  }

  ${({ theme }) => css`
    ${theme.below.tablet`
      div {
        grid-column: span 3;
      }

      div:nth-child(3) {
        grid-column: span 6;
      }
    `}
  `}
`

const gridForSixElements = css`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);

  ${({ theme }) => css`
    ${theme.below.tablet`
      grid-template-columns: repeat(2, 1fr);
    `}
  `}
`

const gridStyles = {
  2: gridForTwoElements,
  3: gridForThreeElements,
  4: gridForFourElements,
  5: gridForFiveElements,
  6: gridForSixElements,
}

export const ContentWrapper = styled(Box)<ContentWrapperProps>`
  ${({ theme, $numBlocks, $borderColor }) => css`
    align-items: normal;
    background-color: ${$borderColor || theme.newColors.grayLine};
    display: grid;
    gap: 1px;
    ${gridStyles[$numBlocks as keyof typeof gridStyles]}
  `}
`

export const Content = styled(Box)<ContentProps>`
  ${({ theme, $reversed }) => css`
    align-items: center;
    background-color: ${theme.newColors.white};
    flex-direction: ${$reversed ? 'column-reverse' : 'column'};
    gap: ${theme.newSpaces.xs}px;
    justify-content: flex-start;
    padding: ${theme.newSpaces.sm}px;
  `}
`

export const Title = styled(Heading)<TitleProps>`
  ${({ theme, $color }) => css`
    color: ${$color || theme.newColors.accent};

    ${theme.getTypography('desktop', 'title', 'title1')}

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'title', 'large')}
    `}
  `}

  text-align: center;
  white-space: pre-wrap;
`

export const TitleContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  ${({ theme }) => css`
    gap: ${theme.newSpaces.xs2}px;
  `}
`

export const Subtitle = styled.span<SubtitleProps>`
  ${({ theme, $color }) => css`
    color: ${$color || theme.newColors.accent};
    white-space: pre-wrap;

    ${theme.getTypography('desktop', 'title', 'title2')}

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'title', 'title4')}
    `}
  `}
`

export const Text = styled(Paragraph)<TextProps>`
  ${({ theme, $color }) => css`
    color: ${$color || theme.newColors.primary};

    ${theme.getTypography('desktop', 'title', 'title2')}

    ${theme.below.mobileXL`
      ${theme.getTypography('mobile', 'body', 'body4')}
    `}
  `}

  text-align: center;
  white-space: pre-wrap;
  width: 100%;
`
